import { useState } from "react";
import { motion } from "framer-motion";
import { Flame, Drumstick, Soup, Fish } from "lucide-react";
import { MaskLines, Reveal } from "./Motion";
import { FLAVOURS, PAIRINGS, HOTSPOTS, IMG } from "../../data/content";

const ICONS = { curry: Flame, tandoor: Drumstick, street: Soup, fish: Fish };

const Stack = ({ active, items }) => (
  <div className="grid [grid-template-areas:'s']">
    {items.map(([key, node]) => (
      <motion.div key={key} initial={false} animate={{ opacity: key === active ? 1 : 0, y: key === active ? 0 : 14 }} transition={{ duration: 0.55 }} aria-hidden={key !== active} className="[grid-area:s]">
        {node}
      </motion.div>
    ))}
  </div>
);

export default function Showcase() {
  const [flavour, setFlavour] = useState("original");
  const f = FLAVOURS[flavour];
  const all = Object.values(FLAVOURS);

  return (
    <section id="ciders" data-orbit className="relative overflow-hidden bg-mist py-24 text-forest-deep lg:py-32" data-testid="showcase-section">
      <motion.div className="absolute inset-0" animate={{ background: `radial-gradient(45% 55% at 25% 45%, ${f.glow}, transparent 70%)` }} transition={{ duration: 1.2 }} />
      <div className="wrap relative grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <div className="relative h-[62svh] lg:sticky lg:top-[9vh] lg:h-[82vh]" data-can-stage data-rot="0" data-variant={flavour} data-hotspots="1" data-snap="1" data-testid="showcase-can-stage">
            <p className="absolute left-0 top-0 font-mono text-[11px] uppercase tracking-[0.22em] text-forest-deep/80">Drag to rotate · tap + for notes</p>
          </div>
        </div>

        <div className="lg:col-span-6 lg:pt-16">
          <p className="eyebrow text-forest">Chapter 05 — The Ciders</p>
          <MaskLines className="mt-6 font-display text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-7xl" lines={["Two ciders.", <span key="b" className="italic text-forest">One mountain.</span>]} />

          <div className="mt-10 inline-flex rounded-full border border-forest-deep/25 bg-white/50 p-1.5" role="tablist" data-testid="flavour-switcher">
            {all.map((x) => (
              <button key={x.id} role="tab" aria-selected={flavour === x.id} data-testid={`flavour-switcher-${x.id}`} onClick={() => setFlavour(x.id)} className="relative rounded-full px-5 py-3 text-sm font-medium uppercase tracking-[0.14em] sm:px-6">
                {flavour === x.id && <motion.span layoutId="flav-pill" className="absolute inset-0 rounded-full" style={{ background: x.accent }} transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
                <span className={`relative ${flavour === x.id ? (x.id === "berry" ? "text-white" : "text-forest-deep") : "text-forest-deep/80"}`}>{x.label}</span>
              </button>
            ))}
          </div>

          <div className="mt-10">
            <Stack active={flavour} items={all.map((x) => [x.id, (
              <div key={x.id}>
                <h3 className="font-display text-4xl font-semibold leading-tight" data-testid={`flavour-name-${x.id}`}>{x.name}</h3>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-forest-deep">{x.desc}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {x.notes.map((n) => <span key={n} className="rounded-full border bg-white/50 px-3 py-1.5 text-xs text-forest-deep" style={{ borderColor: x.id === "berry" ? x.accent : "#007D6C" }}>{n}</span>)}
                </div>
              </div>
            )])} />
          </div>

          <div className="mt-10 grid grid-cols-3 border-y border-forest-deep/20 py-6 font-mono">
            {[["330ml", "Can"], ["<5%", "ABV"], ["Light", "Sparkle"]].map(([a, b]) => (
              <div key={b}><p className="text-3xl text-forest">{a}</p><p className="mt-1 text-[12px] uppercase tracking-[0.2em] text-forest-deep/80">{b}</p></div>
            ))}
          </div>

          <p className="eyebrow mt-12 text-forest">Perfect pairings</p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            {PAIRINGS.map((p, k) => {
              const Icon = ICONS[p.id];
              return (
                <Reveal key={p.id} delay={k * 0.06} className="group rounded-2xl border border-forest-deep/15 bg-white/60 p-5 transition-colors hover:border-forest" data-testid={`pairing-${p.id}`}>
                  <Icon className="h-6 w-6 text-forest transition-transform duration-500 group-hover:-rotate-12" />
                  <p className="mt-4 font-display text-xl font-semibold">{p.title}</p>
                  <p className="mt-1 text-sm text-forest-deep/85">{p.note}</p>
                </Reveal>
              );
            })}
          </div>

          <ul className="mt-10 space-y-3 lg:hidden" aria-label="Can notes">
            {HOTSPOTS.map((h) => <li key={h.id} className="text-sm text-forest-deep"><span className="mr-2 font-mono text-[11px] uppercase tracking-[0.2em] text-forest">{h.title}</span>{h.body[flavour]}</li>)}
          </ul>

          <Reveal className="relative mt-12 overflow-hidden rounded-[1.5rem]">
            <Stack active={flavour} items={all.map((x) => [x.id, <img key={x.id} src={IMG(x.photo)} alt={`${x.name}: launching soon`} className="aspect-[3/2] w-full object-cover" />])} />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_70%_at_50%_45%,transparent,rgba(0,125,108,.45))]" />
            <p className="absolute bottom-4 left-5 font-mono text-[12px] uppercase tracking-[0.22em] text-white">New cans · launching soon</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
